import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import WidgetContainer from "../reusablesComponents/containerWidget";
import CarrouselSponsoredLeft from "../carrouselSponsored/carrouselSponsoredLeft/carrouselSponsoredLeft";
import ListSelectsPlayers from "./listSelectsPlayers/listSelectsPlayers";
import ListPositionPlayers from "./listPositionPlayers/listPositionPlayers";
import ShareButtonSelection from "./shareButtonSelection/shareButtonSelection";
import { selectionMXConfig } from "./configs/selectionMX.config";
import "./simulatorSelectionMX.sass";

const MAX_PLAYERS = 26;
const MAX_GOALKEEPERS = 3;
const MAX_FIELD_PLAYERS = MAX_PLAYERS - MAX_GOALKEEPERS;
const POSITIONS = ["Portero", "Defensa", "Medio", "Delantero"];

const positionFilters = POSITIONS.map((pos, index) => ({ id: pos, name: pos, index }));

export default function SimulatorSelectionMX({ config = selectionMXConfig }) {
  const { data: simulatorSelectionData, backgroundEscudo, title, accentColor, accentColorAlt, selectedColor } = config;

  const playersByPosition = useMemo(() =>
    POSITIONS.reduce((acc, pos) => {
      acc[pos] = simulatorSelectionData.filter((p) => p.posicion === pos);
      return acc;
    }, {}),
  [simulatorSelectionData]);

  const [selectedPlayers, setSelectedPlayers] = useState(
    () => simulatorSelectionData.filter((p) => p.requerido === true)
  );
  const [activePosition, setActivePosition] = useState(null);
  const [dropdownLeft, setDropdownLeft] = useState(0);

  const scrollPositionRef = useRef(0);
  const indexJsonRef = useRef(0);
  const tabsWrapperRef = useRef(null);

  const goalkeepersCount = useMemo(
    () => selectedPlayers.filter((p) => p.posicion === "Portero").length,
    [selectedPlayers]
  );
  const fieldPlayersCount = useMemo(
    () => selectedPlayers.filter((p) => p.posicion !== "Portero").length,
    [selectedPlayers]
  );

  const isPositionDisabled = useCallback((pos) => {
    if (pos === "Portero") return goalkeepersCount >= MAX_GOALKEEPERS;
    return fieldPlayersCount >= MAX_FIELD_PLAYERS;
  }, [goalkeepersCount, fieldPlayersCount]);

  const handleSelectPlayer = useCallback((player) => {
    setSelectedPlayers((prev) => {
      if (prev.some((p) => p.index === player.index)) return prev;
      if (isPositionDisabled(player.posicion)) return prev;
      return [...prev, player];
    });
  }, [isPositionDisabled]);

  const removePlayer = useCallback((index) => {
    setSelectedPlayers((prev) => prev.filter((p) => p.index !== index));
  }, []);

  const recalculateDropdownLeft = useCallback(() => {
    if (!tabsWrapperRef.current || !activePosition) return;
    if (window.innerWidth <= 480) { setDropdownLeft(0); return; }
    const allLis = tabsWrapperRef.current.querySelectorAll(".league-nav li");
    const activeLi = allLis[activePosition.index];
    if (activeLi) {
      const wrapperRect = tabsWrapperRef.current.getBoundingClientRect();
      const liRect = activeLi.getBoundingClientRect();
      setDropdownLeft(liRect.left - wrapperRect.left);
    }
  }, [activePosition]);

  const handleTabSelect = useCallback((league) => {
    setActivePosition((prev) => prev?.id === league.id ? null : league);
    requestAnimationFrame(() => {
      if (!tabsWrapperRef.current) return;
      if (window.innerWidth <= 480) { setDropdownLeft(0); return; }
      const allLis = tabsWrapperRef.current.querySelectorAll(".league-nav li");
      const activeLi = allLis[league.index];
      if (activeLi) {
        const wrapperRect = tabsWrapperRef.current.getBoundingClientRect();
        const liRect = activeLi.getBoundingClientRect();
        setDropdownLeft(liRect.left - wrapperRect.left);
      }
    });
  }, []);


  const currentPlayers = playersByPosition[activePosition?.id] || [];
  const disabled = isPositionDisabled(activePosition?.id);

  // Cerrar el listado al hacer click/touch fuera del tabs-wrapper
  useEffect(() => {
    if (!activePosition) return;
    const handleOutsideClick = (e) => {
      if (tabsWrapperRef.current && !tabsWrapperRef.current.contains(e.target)) {
        setActivePosition(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [activePosition]);

  return (
    <WidgetContainer className="container__simulator-selection-mx">
      <div className="simulator-selection-mx">

        <div className="simulator-selection-mx__summary-header">
          <div className="simulator-selection-mx__summary-titles">
            <h3 className="simulator-selection-mx__summary-title">{title}</h3>
            <span className="simulator-selection-mx__summary-count">{selectedPlayers.length} / {MAX_PLAYERS}</span>
          </div>
          <ShareButtonSelection {...{ selectedPlayers, config }} />
        </div>

        <div className="simulator-selection-mx__tabs-wrapper" ref={tabsWrapperRef}>
          <CarrouselSponsoredLeft
            dataFilters={positionFilters}
            selectedJson={handleTabSelect}
            selectedValue={activePosition}
            setScrollPosition={(val) => {
                scrollPositionRef.current = val;
                recalculateDropdownLeft();
              }}
            scrollPosition={scrollPositionRef.current}
            setIndexJson={(val) => { indexJsonRef.current = val; }}
            showDropdownArrow
          />

          {activePosition && (
            <div className="simulator-selection-mx__player-list" style={{ left: dropdownLeft }}>
              <ListPositionPlayers {...{ currentPlayers, handleSelectPlayer, removePlayer, selectedPlayers, disabled, accentColor, selectedColor }} />
            </div>
          )}
        </div>

        <div
          className="simulator-selection-mx__summary"
          style={{ backgroundImage: `url(${backgroundEscudo})` }}
        >
          <ListSelectsPlayers {...{ POSITIONS, selectedPlayers, removePlayer, accentColor, accentColorAlt }} />
        </div>

      </div>
    </WidgetContainer>
  );
}